import { HELP, type HelpDef } from '../help/content';
import { mobileDisplay, normalizeMobile, readPlate } from '../pages/staff/reception/home/search';
import { foldText, matchesQuery } from './listSearch';
import type { Menu, NavItem } from './menu';

/**
 * The search box under the brand in the staff menu (every login: the venue menu, the reception's menu, security,
 * the accountant, shop logins and the super admin inside a shop), as pure functions so unit tests can read them:
 *
 *   filterMenu   narrows the menu to the pages whose label, section or "How this works" text has every word typed,
 *                ignoring case, accents and spaces ("gate inside" finds "Gate & vehicles inside", "dailypulse" finds
 *                "Daily pulse"). Pages whose label matches come first; sections with no match disappear.
 *   recordJumps  "Find" rows under the pages: what the text looks like (a number plate, a mobile, a bill number, a
 *                name) opens the list page that can look it up, with the text in its search box (?q=) — only for
 *                pages in this login's menu, so a shop login never gets venue pages and security never gets staff.
 */

/** Route → the words of its help entry (title and lines, every role's variant), for matching. */
export type HelpIndex = Record<string, string[]>;

export function buildHelpIndex(defs: readonly HelpDef[] = HELP): HelpIndex {
  const index: HelpIndex = {};
  for (const def of defs) {
    const words = [def.title, ...def.lines];
    for (const variant of Object.values(def.roles ?? {})) {
      if (variant?.title) words.push(variant.title);
      if (variant?.lines) words.push(...variant.lines);
    }
    index[def.path ? `/admin/${def.path}` : '/admin'] = words;
  }
  return index;
}

export const HELP_INDEX = buildHelpIndex();

export interface FilteredMenu extends Menu {
  /** The page Enter opens: the best match, or null when nothing (or nothing was typed). */
  first: NavItem | null;
  /** Some page's name has every word typed (the "Find" rows for a name then stay out of the way). */
  labelHit: boolean;
}

/** 3 when the label has every word, 2 with the section's name, 1 with the help text, 0 when it does not match. */
function score(item: NavItem, section: string, q: string, help: HelpIndex): number {
  if (matchesQuery(q, [item.label])) return 3;
  if (matchesQuery(q, [item.label, section])) return 2;
  if (matchesQuery(q, [item.label, section, ...(help[item.to] ?? [])])) return 1;
  return 0;
}

/** The menu narrowed to the query (the same sections when nothing is typed); see the module comment. */
export function filterMenu(menu: Menu, query: string, help: HelpIndex = HELP_INDEX): FilteredMenu {
  const q = foldText(query);
  if (!q) return { sections: menu.sections, first: null, labelHit: false };
  const scored = menu.sections
    .map((s) => {
      const items = s.items.map((item) => ({ item, score: score(item, s.title, q, help) })).filter((x) => x.score > 0);
      // Stable sort: equal scores keep the menu's order.
      items.sort((a, b) => b.score - a.score);
      return { title: s.title, items: items.map((x) => x.item), best: items[0]?.score ?? 0 };
    })
    .filter((s) => s.items.length > 0)
    .sort((a, b) => b.best - a.best);
  return { sections: scored.map(({ title, items }) => ({ title, items })), first: scored[0]?.items[0] ?? null, labelHit: (scored[0]?.best ?? 0) === 3 };
}

// ------------------------------------------------------------------ records

/** What the typed text looks like. */
export type RecordKind = 'plate' | 'mobile' | 'bill' | 'digits' | 'name' | 'text';

export interface RecordJump {
  /** The list page with the search in its address, e.g. /admin/customers?q=9847012345. */
  to: string;
  /** The page's name as the row says it, e.g. "Vehicles & visits". */
  page: string;
  /** The row: "Vehicles & visits: KL 11 B 2248". */
  label: string;
  kind: RecordKind;
}

interface Finder {
  /** The page's route, or the routes in order of preference (the first one in the menu wins). */
  to: string | string[];
  page: string;
  kinds: RecordKind[];
}

/**
 * The list pages that can look a record up from ?q= (each already searches on the server or in the browser), and
 * what each one understands. Bookings and the rent pages have no such search box, so they are not here.
 */
const FINDERS: Finder[] = [
  { to: '/admin/customers', page: 'Customers', kinds: ['mobile', 'plate', 'digits', 'name'] },
  // The visit history searches by registration; the gate board, for a login without it, finds a vehicle too.
  { to: ['/admin/visits', '/admin/gate'], page: 'Vehicles & visits', kinds: ['plate', 'digits'] },
  // Bills: bill number or serial, customer name, part of a mobile (the server's rule).
  { to: '/admin/bills', page: 'Bills', kinds: ['bill', 'digits', 'mobile', 'name', 'text'] },
  { to: '/admin/users', page: 'Staff', kinds: ['name', 'mobile'] },
  { to: '/admin/employees', page: 'Employees', kinds: ['name', 'mobile', 'text'] },
  { to: '/admin/outlets', page: 'Shops & outlets', kinds: ['name'] },
  { to: '/admin/payments', page: 'Payments', kinds: ['text'] },
];

/** Letters (any script, with its vowel signs), spaces, dots, apostrophes and hyphens: a name. */
const NAMEISH = /^[\p{L}\p{M}\s.'’-]+$/u;
/** Only digits and the separators people type in phone numbers. */
const DIGITISH = /^[+\d\s()-]+$/;

export interface ParsedRecord {
  kinds: RecordKind[];
  /** The text as the row shows it ("KL 11 B 2248", "98470 12345", "#123", "ravi"). */
  shown: string;
  /** What goes into the page's search box. */
  q: string;
}

/**
 * How the text is read:
 *   #123, 1–3 digits        → a bill or ticket number
 *   4–5 digits              → a bill number, the last digits of a plate, part of a mobile
 *   6–10 digits (or +91 …)  → a mobile
 *   KL 11 B 2248, kl11b2248 → a number plate (also part of one: KL11, AB1234)
 *   ravi, Anjali Nair       → a name (3 letters at least, so typing the start of a page's name stays quiet)
 *   P261021-0012, UTR…      → a reference (letters and digits that are not a plate)
 */
export function parseRecord(raw: string): ParsedRecord | null {
  const text = raw.trim().replace(/\s+/g, ' ');
  if (!text) return null;
  const hash = /^#\s*(\d{1,12})$/.exec(text);
  if (hash) return { kinds: ['bill'], shown: `#${hash[1]}`, q: hash[1] };
  if (DIGITISH.test(text)) {
    const digits = text.replace(/\D/g, '');
    if (!digits) return null;
    const mobile = normalizeMobile(text);
    if (mobile) return { kinds: ['mobile'], shown: mobileDisplay(mobile), q: mobile.startsWith('+91') ? mobile.slice(3) : mobile };
    if (digits.length <= 3) return { kinds: ['bill'], shown: digits, q: digits };
    if (digits.length <= 5) return { kinds: ['bill', 'plate', 'digits'], shown: digits, q: digits };
    if (digits.length <= 10) return { kinds: ['mobile'], shown: digits, q: digits };
    return null;
  }
  if (NAMEISH.test(text)) {
    const letters = text.replace(/[^\p{L}]/gu, '').length;
    return letters >= 3 ? { kinds: ['name'], shown: text, q: text } : null;
  }
  if (/^[A-Za-z0-9\s.-]+$/.test(text) && /\d/.test(text) && /[A-Za-z]/.test(text)) {
    const plate = readPlate(text);
    if (plate) return { kinds: ['plate'], shown: plate.display, q: plate.compact };
    return text.length >= 4 ? { kinds: ['text'], shown: text, q: text } : null;
  }
  return null;
}

/** The "Find" rows for the text, for the pages this login's menu has (none when the text is not a record). */
export function recordJumps(query: string, menu: Menu): RecordJump[] {
  const parsed = parseRecord(query);
  if (!parsed) return [];
  const offered = new Set(menu.sections.flatMap((s) => s.items.map((i) => i.to)));
  const rows: RecordJump[] = [];
  for (const f of FINDERS) {
    const kind = f.kinds.find((k) => parsed.kinds.includes(k));
    if (!kind) continue;
    const to = (Array.isArray(f.to) ? f.to : [f.to]).find((t) => offered.has(t));
    if (!to) continue;
    rows.push({ to: `${to}?q=${encodeURIComponent(parsed.q)}`, page: f.page, label: `${f.page}: ${parsed.shown}`, kind });
  }
  return rows;
}
